import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
/* Utils */
import { getInventoryListTrackingData, getRadiusValue } from 'site-modules/shared/utils/inventory/srp-utils';
/* Tracking */
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/inventory/srp';
/* Models */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { SEOModel } from 'client/data/models/seo';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
/* Components */
import { InventoryDependencyContext } from 'site-modules/shared/components/inventory/inventory-dependency/inventory-dependency-context';
import { usurpDependencyContextValue } from 'site-modules/shared/components/inventory/inventory-dependency/usurp-dependency-context-value';
import { CarBuyingOnline } from 'site-modules/car-buying-online/pages/srp/car-buying-online';

export const CarBuyingOnlineContainerUI = ({
  inventoryData = null,
  visitorLocation = {},
  urlContext = {},
  seoHeadContent = {},
  selectedFacets = null,
  location = {},
  match = {},
  disableInventoryMLPhotoSort = false,
  isPrequalified = false,
  ...restProps
}) => {
  /*
Delete this code if <SEOWrapper> in client/site-modules/shared/pages/seo.jsx begins re-rending on the client side.
*/
  useEffect(() => {
    if (seoHeadContent) {
      const { title, description } = seoHeadContent;
      if (title !== document?.title) {
        document.title = title;
        document.querySelector("meta[name='title']").setAttribute('content', title);
        document.querySelector("meta[name='description']").setAttribute('content', description);
      }
    }
  }, [seoHeadContent]);

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  if (!selectedFacets) return null;

  const dmaRank = visitorLocation?.dmaRank || 0;
  const selectedRadius = !!selectedFacets.radius?.[0];
  const attrRadius = (inventoryData?.attributes?.radius || '').toString();
  const radius = parseInt(getRadiusValue(dmaRank, selectedRadius, attrRadius), 10);
  const includeDeliveryFee = selectedFacets.deliveryFee === 'true';

  const listTrackingData = getInventoryListTrackingData({
    radius,
    inventoryData,
    selectedFacets,
    urlContext,
  });

  const CarBuyingOnlineProps = {
    ...{
      inventoryData,
      visitorLocation,
      urlContext,
      seoHeadContent,
      selectedFacets,
      ...restProps,
      location,
      match,
      disableInventoryMLPhotoSort,
      isPrequalified,
    },
    radius,
    listTrackingData,
    ...(includeDeliveryFee && { includeDeliveryFee }),
  };

  return (
    <InventoryDependencyContext.Provider value={usurpDependencyContextValue}>
      <CarBuyingOnline {...CarBuyingOnlineProps} />
    </InventoryDependencyContext.Provider>
  );
};

CarBuyingOnlineContainerUI.propTypes = {
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
    seoInfo: PropTypes.shape({}),
  }),
  isMobile: PropTypes.bool.isRequired,
  setModelValue: PropTypes.func.isRequired,
  selectedFacets: PropTypes.shape({}),
  urlContext: PropTypes.shape({}),
  location: PropTypes.shape({}),
  zipCode: PropTypes.string.isRequired,
  visitorLocation: VisitorEntities.Location,
  match: PropTypes.shape({
    params: PropTypes.shape({}),
  }),
  disableInventoryMLPhotoSort: PropTypes.bool,
  seoHeadContent: PropTypes.shape({}),
  isPrequalified: PropTypes.bool,
};

CarBuyingOnlineContainerUI.displayName = 'CarBuyingOnlineContainerUI';

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  disableInventoryMLPhotoSort: !!get(state, 'featureFlags["disable-inventory-ml-photo-sort"]'),
  isPrequalified: !!get(state, 'financing.prequalificationData'),
});

export const stateToPropsConfig = {
  urlContext: bindToPath(URL_CONTEXT_PATH, UrlModel),
  inventoryData: bindToPath('searchResults', InventoryModel),
  selectedFacets: bindToPath('searchResultsFilter', InventoryModel),
  visitorLocation: bindToPath('location', VisitorModel),
  zipCode: bindToPath('location.zipCode', VisitorModel),
  seoHeadContent: bindToPath('headContent', SEOModel),
};

export const CarBuyingOnlineContainer = connect(mapStateToProps)(
  connectToModel(CarBuyingOnlineContainerUI, stateToPropsConfig)
);
